import logWrapper from '../../../share/logWrapper';

const TRANSACTION = {
  READ_ONLY: 'readonly',
  READ_WRITE: 'readwrite',
  VERSION_CHANGE: 'versionchange '
};
const DB_VERSION = 1;
const DB_NAME = 'DataMonitorDB';
// IndexedDBの使用に必要なストレージ容量。2時間のデータで250MB以内だが、多めに1GBとする
const NECESSARY_STORAGE = 1024 * 1024 * 1024;

export const STORE_NAME = {
  DATA_POINT: 'DataPointStore'
};
export const DATA_POINT_STORE = {
  KEY_PATH: 'id',
  ID_TYPE: {
    SHOW_DATA: 'show',
    DECIMATED_DATA: 'decimated'
  }
};

/** IndexedDB操作クラス */
export class IndexedDbManager {
  constructor() {
    this.dbInstance = null;
    // コンストラクタではawaitできないため、コールバックでプロパティ設定
    IndexedDbManager.checkStorageUsage().then(result => {
      this.available = result;
    });
  }

  /**
   * IndexedDBが使用可能か否かを返す
   * @returns {boolean} IndexedDBが使用可能か否か
   */
  isAvailable() {
    return this.available;
  }

  __callBacks(resolve, reject) {
    return [(result) => resolve(result), (error) => reject(error)];
  }

  /** DB開始のPromise wrapper */
  async asyncOpenDB() {
    return new Promise((resolve, reject) => {
      this.openDB(...this.__callBacks(resolve, reject));
    });
  }

  /** データ取得のPromise wrapper */
  async asyncRead(storeName, id) {
    return new Promise((resolve, reject) => {
      this.read(storeName, id, ...this.__callBacks(resolve, reject));
    });
  }

  /** データ追加のPromise wrapper */
  async asyncAdd(storeName, data) {
    return new Promise((resolve, reject) => {
      this.add(storeName, data, ...this.__callBacks(resolve, reject));
    });
  }

  /** データクリアのPromise wrapper */
  async asyncClearStore(storeName) {
    return new Promise((resolve, reject) => {
      this.clearStore(storeName, ...this.__callBacks(resolve, reject));
    });
  }

  /** レコード数取得のPromise wrapper */
  async asyncCount(storeName) {
    return new Promise((resolve, reject) => {
      this.count(storeName, ...this.__callBacks(resolve, reject));
    });
  }

  /**
   * DB開始
   * @param {function} onSuccess 成功時のコールバック処理
   * @param {function} onError エラー時のコールバック処理
   */
  openDB(onSuccess = () => { }, onError = () => { }) {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error(event);
      onError(`Database open error: ${event.target.error?.message}`);
    };

    request.onblocked = function (event) {
      console.warn(event);
      event.target.result.close();
    };

    // 初回のDB新規作成時のみ呼ばれるイベント
    // オブジェクトストアやインデックス作成は、ここで行う
    request.onupgradeneeded = function (event) {
      logWrapper.log('upgrade indexedDB complete');
      const db = event.target.result;
      // オブジェクトストア作成(テーブル相当)
      db.createObjectStore(STORE_NAME.DATA_POINT, { keyPath: DATA_POINT_STORE.KEY_PATH, autoIncrement: true });
    };

    const self = this;
    request.onsuccess = (event) => {
      logWrapper.log('open indexedDB complete');
      self.dbInstance = event.target.result;
      onSuccess();
    };
  }

  /** DBクローズ */
  closeDB() {
    if (!this.dbInstance) return;
    this.dbInstance.close();
    logWrapper.log('close indexedDB complete');
  }

  /**
   * データ取得
   * @param {string} storeName 取得先オブジェクトストア名
   * @param {string} id 取得するレコードのID
   * @param {function} onSuccess 成功時のコールバック処理
   * @param {function} onError エラー時のコールバック処理
   */
  read(storeName, id, onSuccess = () => { }, onError = () => { }) {
    if (!this.dbInstance) return;
    const request = this.dbInstance
      .transaction([storeName], TRANSACTION.READ_ONLY)
      .objectStore(storeName)
      .get(id);

    request.onerror = (event) => {
      console.error(event);
      onError(`Database read error: ${event.target.error?.message}`);
    };

    request.onsuccess = () => {
      onSuccess(request.result);
    };
  }

  /**
   * データ追加
   * @param {string} storeName 追加先オブジェクトストア名
   * @param {object} data 追加するデータ
   * @param {function} onSuccess 成功時のコールバック処理
   * @param {function} onError エラー時のコールバック処理
   */
  add(storeName, data, onSuccess = () => { }, onError = () => { }) {
    // 追加処理は使用不可の場合処理しない
    if (!this.dbInstance || !this.available) return;
    const request = this.dbInstance
      .transaction([storeName], TRANSACTION.READ_WRITE)
      .objectStore(storeName)
      .add(data);

    request.onerror = (event) => {
      console.error(event);
      if (event.target.error?.name === 'QuotaExceededError') {
        console.warn('Insufficient capacity');
        // 容量制限エラーの場合、DB使用不可に変更のみ行い、エラーにしない
        this.available = false;
        onSuccess(event);
      } else {
        onError(`Database add error: ${event.target.error?.message}`);
      }
    };

    request.onsuccess = (event) => {
      onSuccess(event);
    };
  }

  /**
   * データクリア
   * @param {string} storeName 対象オブジェクトストア名
   * @param {function} onSuccess 成功時のコールバック処理
   * @param {function} onError エラー時のコールバック処理
   */
  clearStore(storeName, onSuccess = () => { }, onError = () => { }) {
    if (!this.dbInstance) return;
    const request = this.dbInstance
      .transaction([storeName], TRANSACTION.READ_WRITE)
      .objectStore(storeName)
      .clear();

    request.onsuccess = function () {
      logWrapper.log('clear indexedDB complete');
      onSuccess();
    };

    request.onerror = function (event) {
      console.error(event);
      onError(`Clear store error: ${event.target.error?.message}`);
    };
  }

  /**
   * DB削除
   * @param {function} onSuccess 成功時のコールバック処理
   * @param {function} onError エラー時のコールバック処理
   */
  deleteDB(onSuccess = () => { }, onError = () => { }) {
    const request = indexedDB.deleteDatabase(DB_NAME);

    request.onsuccess = function () {
      logWrapper.log('delete indexedDB complete');
      onSuccess();
    };

    request.onerror = function (event) {
      console.error(event);
      onError(`Database delete error: ${event.target.error?.message}`);
    };

    const self = this;
    request.onblocked = function (event) {
      // closeしていないとpendingになる
      logWrapper.log('deleting');
      self.closeDB();
      logWrapper.log(event);
    };
  }

  /**
   * レコード数取得
   * @param {string} storeName 取得先オブジェクトストア名
   * @param {function} onSuccess 成功時のコールバック処理
   * @param {function} onError エラー時のコールバック処理
   */
  count(storeName, onSuccess = () => { }, onError = () => { }) {
    if (!this.dbInstance) return;
    const request = this.dbInstance
      .transaction([storeName], TRANSACTION.READ_ONLY)
      .objectStore(storeName)
      .count();

    request.onerror = (event) => {
      console.error(event);
      onError(`Database count error: ${event.target.error?.message}`);
    };

    request.onsuccess = () => {
      onSuccess(request.result);
    };
  }

  /**
   * IndexedDBが使用できるストレージ容量を確認。
   * @returns {boolean} IndexedDB使用可否。データ容量取得APIが対応していない場合も可能とする。
   */
  static async checkStorageUsage() {
    if (navigator?.storage?.estimate && typeof navigator.storage.estimate === 'function') {
      const estimate = await navigator.storage.estimate();
      logWrapper.log(`quota: ${estimate.quota}, usage: ${estimate.usage}`);
      return NECESSARY_STORAGE < (estimate.quota - estimate.usage);
    } else {
      console.warn('Not support StorageManager API');
      return true;
    }
  }
}
